import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { useInsightsData } from '@/hooks/useInsightsData'
import { PeriodSelector } from '@/components/dashboard/filters'
import type { DatePeriod } from '@/components/dashboard/filters'
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  LineChart,
  Line,
  CartesianGrid,
} from 'recharts'
import { getCountryFlag, getDeviceEmoji, getOSEmoji } from '@/lib/icons'

const COLORS = [
  'var(--color-primary-500)',
  'var(--color-primary-400)',
  'var(--color-primary-300)',
  '#818cf8',
  '#a78bfa',
  '#c4b5fd',
  '#10b981',
  '#6ee7b7',
]

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function formatCurrency(value: number): string {
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}k`
  return `$${value.toFixed(0)}`
}

function PieChartCard({
  title,
  icon,
  data,
  isLoading,
  renderLabel,
}: {
  title: string
  icon: string
  data: { label: string; revenue: number; percentage: number }[]
  isLoading?: boolean
  renderLabel?: (label: string) => React.ReactNode
}) {
  if (isLoading) {
    return (
      <div className="section-card">
        <div className="section-header">
          <span>{icon}</span>
          <span>{title}</span>
        </div>
        <div className="p-8 flex items-center justify-center">
          <div className="animate-spin w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full" />
        </div>
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className="section-card">
        <div className="section-header">
          <span>{icon}</span>
          <span>{title}</span>
        </div>
        <div className="p-8 text-center text-gray-500">
          <span className="text-3xl">📊</span>
          <p className="mt-2">No data available</p>
        </div>
      </div>
    )
  }

  return (
    <div className="section-card">
      <div className="section-header">
        <span>{icon}</span>
        <span>{title}</span>
      </div>
      <div className="p-4">
        <div className="flex items-center gap-4">
          <div className="w-36 h-36">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={35}
                  outerRadius={55}
                  paddingAngle={2}
                  dataKey="revenue"
                >
                  {data.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 space-y-2">
            {data.slice(0, 5).map((item, index) => (
              <div key={item.label} className="flex items-center gap-2 text-sm">
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <span className="flex-1 truncate">
                  {renderLabel ? renderLabel(item.label) : item.label}
                </span>
                <span className="text-gray-500">{item.percentage.toFixed(1)}%</span>
                <span className="font-medium text-gray-900">{formatCurrency(item.revenue)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function HistogramCard({
  title,
  icon,
  data,
  isLoading,
  avgValue,
  avgLabel,
}: {
  title: string
  icon: string
  data: { label: string; count: number; percentage: number }[]
  isLoading?: boolean
  avgValue?: number
  avgLabel?: string
}) {
  if (isLoading) {
    return (
      <div className="section-card">
        <div className="section-header">
          <span>{icon}</span>
          <span>{title}</span>
        </div>
        <div className="p-8 flex items-center justify-center">
          <div className="animate-spin w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full" />
        </div>
      </div>
    )
  }

  const maxCount = Math.max(...data.map(d => d.count), 1)

  return (
    <div className="section-card">
      <div className="section-header">
        <span>{icon}</span>
        <span>{title}</span>
      </div>
      <div className="p-4">
        {avgValue !== undefined && (
          <div className="mb-4 text-center">
            <span className="text-3xl font-bold text-gray-900">
              {avgValue.toFixed(1)}
            </span>
            <span className="text-gray-500 ml-1">{avgLabel}</span>
          </div>
        )}
        <div className="space-y-2">
          {data.map((item) => (
            <div key={item.label} className="flex items-center gap-2">
              <span className="w-20 text-xs text-gray-600 text-right">{item.label}</span>
              <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-400 rounded-full transition-all"
                  style={{ width: `${(item.count / maxCount) * 100}%` }}
                />
              </div>
              <span className="w-12 text-xs text-gray-500 text-right">{item.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function HeatmapCard({
  title,
  icon,
  data,
  isLoading,
}: {
  title: string
  icon: string
  data: { dayOfWeek: number; hour: number; conversions: number }[]
  isLoading?: boolean
}) {
  if (isLoading) {
    return (
      <div className="section-card lg:col-span-2">
        <div className="section-header">
          <span>{icon}</span>
          <span>{title}</span>
        </div>
        <div className="p-8 flex items-center justify-center">
          <div className="animate-spin w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full" />
        </div>
      </div>
    )
  }

  const maxConversions = Math.max(...data.map(d => d.conversions), 1)

  // Group data by day
  const gridData = DAYS.map((day, dayIndex) => ({
    day,
    hours: Array.from({ length: 24 }, (_, hour) => {
      const cell = data.find(d => d.dayOfWeek === dayIndex && d.hour === hour)
      return {
        hour,
        conversions: cell?.conversions || 0,
      }
    }),
  }))

  const getIntensity = (conversions: number): string => {
    if (conversions === 0) return 'bg-gray-100'
    const ratio = conversions / maxConversions
    if (ratio < 0.25) return 'bg-primary-100'
    if (ratio < 0.5) return 'bg-primary-200'
    if (ratio < 0.75) return 'bg-primary-300'
    return 'bg-primary-400'
  }

  return (
    <div className="section-card lg:col-span-2">
      <div className="section-header">
        <span>{icon}</span>
        <span>{title}</span>
      </div>
      <div className="p-4 overflow-x-auto">
        <div className="min-w-[600px]">
          {/* Hour labels */}
          <div className="flex mb-1">
            <div className="w-12" />
            {[0, 3, 6, 9, 12, 15, 18, 21].map(hour => (
              <div
                key={hour}
                className="text-[10px] text-gray-400"
                style={{ width: `${(24 / 8) * 100 / 24}%`, marginLeft: hour === 0 ? 0 : 'auto' }}
              >
                {hour}:00
              </div>
            ))}
          </div>

          {/* Grid */}
          {gridData.map((row) => (
            <div key={row.day} className="flex items-center gap-1 mb-1">
              <div className="w-10 text-xs text-gray-500 text-right pr-2">{row.day}</div>
              <div className="flex-1 flex gap-0.5">
                {row.hours.map((cell) => (
                  <div
                    key={cell.hour}
                    className={`flex-1 h-4 rounded-sm ${getIntensity(cell.conversions)} transition-colors cursor-default`}
                    title={`${row.day} ${cell.hour}:00 - ${cell.conversions} conversions`}
                  />
                ))}
              </div>
            </div>
          ))}

          {/* Legend */}
          <div className="flex items-center justify-end gap-2 mt-4 text-xs text-gray-500">
            <span>Less</span>
            <div className="flex gap-0.5">
              <div className="w-3 h-3 rounded-sm bg-gray-100" />
              <div className="w-3 h-3 rounded-sm bg-primary-100" />
              <div className="w-3 h-3 rounded-sm bg-primary-200" />
              <div className="w-3 h-3 rounded-sm bg-primary-300" />
              <div className="w-3 h-3 rounded-sm bg-primary-400" />
            </div>
            <span>More</span>
          </div>
        </div>
      </div>
    </div>
  )
}

function TopEventsCard({
  title,
  icon,
  data,
  isLoading,
}: {
  title: string
  icon: string
  data: { eventName: string; count: number; conversionRate: number }[]
  isLoading?: boolean
}) {
  if (isLoading) {
    return (
      <div className="section-card">
        <div className="section-header">
          <span>{icon}</span>
          <span>{title}</span>
        </div>
        <div className="p-8 flex items-center justify-center">
          <div className="animate-spin w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full" />
        </div>
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className="section-card">
        <div className="section-header">
          <span>{icon}</span>
          <span>{title}</span>
        </div>
        <div className="p-8 text-center text-gray-500">
          <span className="text-3xl">🎯</span>
          <p className="mt-2">No events tracked yet</p>
        </div>
      </div>
    )
  }

  return (
    <div className="section-card">
      <div className="section-header">
        <span>{icon}</span>
        <span>{title}</span>
      </div>
      <div className="divide-y divide-gray-100">
        {data.slice(0, 8).map((event, index) => (
          <div key={event.eventName} className="px-4 py-3 flex items-center gap-3">
            <span className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 text-xs font-medium text-gray-600">
              {index + 1}
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-gray-900 truncate">{event.eventName}</div>
              <div className="text-xs text-gray-500">{event.count} occurrences</div>
            </div>
            <div className="text-right">
              <div className="font-medium text-primary-600">{event.conversionRate.toFixed(1)}%</div>
              <div className="text-xs text-gray-500">conversion</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function RevenueGrowthCard({
  title,
  icon,
  data,
  isLoading,
}: {
  title: string
  icon: string
  data: { date: string; revenuePerVisitor: number }[]
  isLoading?: boolean
}) {
  if (isLoading) {
    return (
      <div className="section-card lg:col-span-2">
        <div className="section-header">
          <span>{icon}</span>
          <span>{title}</span>
        </div>
        <div className="p-8 flex items-center justify-center">
          <div className="animate-spin w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full" />
        </div>
      </div>
    )
  }

  return (
    <div className="section-card lg:col-span-2">
      <div className="section-header">
        <span>{icon}</span>
        <span>{title}</span>
      </div>
      <div className="p-4">
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => value.slice(5)}
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                axisLine={{ stroke: '#e5e7eb' }}
              />
              <YAxis
                tickFormatter={(value) => `$${value.toFixed(2)}`}
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                axisLine={{ stroke: '#e5e7eb' }}
                width={50}
              />
              <Tooltip
                formatter={(value: number) => [`$${value.toFixed(2)}`, 'Revenue/Visitor']}
                labelFormatter={(label) => label}
                contentStyle={{
                  backgroundColor: '#1f2937',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '12px',
                }}
              />
              <Line
                type="monotone"
                dataKey="revenuePerVisitor"
                stroke="var(--color-primary-500)"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: 'var(--color-primary-500)' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}

export default function InsightsPage() {
  const { site } = useAuthStore()
  const [period, setPeriod] = useState<DatePeriod>('last30d')

  const {
    revenueByDevice,
    revenueByOS,
    revenueByCountry,
    revenueBySource,
    daysToConvert,
    visitsToConvert,
    revenueGrowth,
    topEvents,
    conversionHeatmap,
    totalRevenue,
    totalConversions,
    avgDaysToConvert,
    avgVisitsToConvert,
    isLoading,
  } = useInsightsData(site?.id, period)

  // No site configured
  if (!isLoading && !site) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">No site configured</h2>
        <p className="text-gray-600 mb-4">Add your website to start tracking analytics.</p>
        <Link to="/settings" className="btn-primary">Add your site</Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Insights</h1>
          <p className="text-gray-600">Revenue patterns and conversion analysis</p>
        </div>
        <PeriodSelector value={period} onChange={setPeriod} />
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-sm text-gray-500">Total Revenue</div>
          <div className="text-2xl font-bold text-gray-900">{formatCurrency(totalRevenue)}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-sm text-gray-500">Conversions</div>
          <div className="text-2xl font-bold text-gray-900">{totalConversions}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-sm text-gray-500">Avg Days to Convert</div>
          <div className="text-2xl font-bold text-gray-900">{avgDaysToConvert.toFixed(1)}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-sm text-gray-500">Avg Visits to Convert</div>
          <div className="text-2xl font-bold text-gray-900">{avgVisitsToConvert.toFixed(1)}</div>
        </div>
      </div>

      {/* Revenue Growth Chart */}
      <RevenueGrowthCard
        title="Revenue per Visitor"
        icon="📈"
        data={revenueGrowth}
        isLoading={isLoading}
      />

      {/* Revenue Breakdown Charts */}
      <div className="grid md:grid-cols-2 gap-6">
        <PieChartCard
          title="Revenue by Device"
          icon="📱"
          data={revenueByDevice}
          isLoading={isLoading}
          renderLabel={(label) => (
            <span className="flex items-center gap-1">
              {getDeviceEmoji(label.toLowerCase())} {label}
            </span>
          )}
        />
        <PieChartCard
          title="Revenue by OS"
          icon="💻"
          data={revenueByOS}
          isLoading={isLoading}
          renderLabel={(label) => (
            <span className="flex items-center gap-1">
              {getOSEmoji(label)} {label}
            </span>
          )}
        />
        <PieChartCard
          title="Revenue by Country"
          icon="🌍"
          data={revenueByCountry}
          isLoading={isLoading}
          renderLabel={(label) => (
            <span className="flex items-center gap-1">
              {getCountryFlag(label)} {label}
            </span>
          )}
        />
        <PieChartCard
          title="Revenue by Source"
          icon="📡"
          data={revenueBySource}
          isLoading={isLoading}
        />
      </div>

      {/* Conversion Journey */}
      <div className="grid md:grid-cols-2 gap-6">
        <HistogramCard
          title="Days to Convert"
          icon="📅"
          data={daysToConvert}
          isLoading={isLoading}
          avgValue={avgDaysToConvert}
          avgLabel="days average"
        />
        <HistogramCard
          title="Visits to Convert"
          icon="🔄"
          data={visitsToConvert}
          isLoading={isLoading}
          avgValue={avgVisitsToConvert}
          avgLabel="visits average"
        />
      </div>

      {/* Conversion Heatmap & Top Events */}
      <div className="grid lg:grid-cols-3 gap-6">
        <HeatmapCard
          title="Conversion Heatmap"
          icon="🔥"
          data={conversionHeatmap}
          isLoading={isLoading}
        />
        <TopEventsCard
          title="Top Converting Events"
          icon="🎯"
          data={topEvents}
          isLoading={isLoading}
        />
      </div>
    </div>
  )
}
